/**
 * App providers: studio state + license. Kept in one place so panels can read
 * and patch the project without prop drilling.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
} from 'react'
import {
  applyPrefs,
  INITIAL_STUDIO_STATE,
  prefsFromState,
  PREFS_STORAGE_KEY,
  studioReducer,
  type StudioAction,
  type StudioPrefs,
  type StudioState,
} from './studioReducer'
import { createId } from '../lib/id'
import {
  activateLicense,
  clearLicense,
  createFreeLicense,
  isPro,
  loadLicense,
  saveLicense,
  type License,
  type LicensePlan,
} from '../lib/license'
import type { Notice, NoticeTone } from './types'

interface StudioContextValue {
  state: StudioState
  dispatch: Dispatch<StudioAction>
  notice: (text: string, tone?: NoticeTone) => void
  dismissNotice: () => void
  currentNotice: Notice | null
}

const StudioContext = createContext<StudioContextValue | null>(null)

function readPrefs(): Partial<StudioPrefs> | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(PREFS_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Partial<StudioPrefs>) : null
  } catch {
    return null
  }
}

function writePrefs(state: StudioState): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefsFromState(state)))
  } catch {
    /* storage blocked — preferences just won't persist */
  }
}

export function StudioProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [state, dispatch] = useReducer(studioReducer, INITIAL_STUDIO_STATE, (initial) =>
    applyPrefs(initial, readPrefs()),
  )

  const [noticeState, setNoticeState] = useState<Notice | null>(null)
  const noticeTimer = useRef<number | null>(null)

  const notice = useCallback((text: string, tone: NoticeTone = 'info') => {
    setNoticeState({ id: createId('notice'), tone, text })
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current)
    noticeTimer.current = window.setTimeout(() => setNoticeState(null), 6000)
  }, [])

  const dismissNotice = useCallback(() => {
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current)
    setNoticeState(null)
  }, [])

  useEffect(() => () => {
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current)
  }, [])

  // Persist the preference slice (never the recording itself).
  useEffect(() => {
    writePrefs(state)
  }, [state])

  const value = useMemo<StudioContextValue>(
    () => ({
      state,
      dispatch,
      notice: (text, tone) => notice(text, tone ?? 'info'),
      dismissNotice,
      currentNotice: noticeState,
    }),
    [state, dispatch, notice, dismissNotice, noticeState],
  )

  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>
}

export function useStudio(): StudioContextValue {
  const context = useContext(StudioContext)
  if (!context) throw new Error('useStudio must be used inside <StudioProvider>.')
  return context
}

interface LicenseContextValue {
  license: License
  pro: boolean
  activate: (key: string, plan: LicensePlan) => { ok: boolean; error?: string }
  deactivate: () => void
}

const LicenseContext = createContext<LicenseContextValue | null>(null)

export function LicenseProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [license, setLicense] = useState<License>(() => loadLicense())

  const activate = useCallback((key: string, plan: LicensePlan) => {
    const result = activateLicense(key, plan)
    if (result.ok) {
      setLicense(result.license)
      saveLicense(result.license)
    }
    return { ok: result.ok, error: result.error }
  }, [])

  const deactivate = useCallback(() => {
    const free = createFreeLicense()
    setLicense(free)
    clearLicense()
  }, [])

  const value = useMemo<LicenseContextValue>(
    () => ({ license, pro: isPro(license), activate, deactivate }),
    [license, activate, deactivate],
  )

  return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>
}

export function useLicense(): LicenseContextValue {
  const context = useContext(LicenseContext)
  if (!context) throw new Error('useLicense must be used inside <LicenseProvider>.')
  return context
}
